import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CalendarDays, MessageSquareText, Brain } from 'lucide-react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Permissions } from '@/lib/permissions'
import {
  useTokenAnalyticsByWorkflow,
  useTokenAnalyticsByUser,
  useCopilotAnalyticsByWorkflow,
  useCopilotAnalyticsByUser,
} from '@/hooks/useAnalytics'
import { useProjectCopilotAuthAvailability } from '@/hooks/useProjectSettings'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { TabBar } from '@/components/TabBar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ByProjectIcon, ByUsersIcon, EditSquareIcon, MoneyIcon, ProjectIcon, TextCompareIcon, TokenKeyIcon, WorkflowDefinitionOutlineIcon } from '@/components/icons'
import { ProjectSectionHeader } from '@/components/projects'
import type { LinkedEntityScope } from '@/types'
import { cn } from '@/lib/utils'

const SCOPE_ICON_MAP: Record<LinkedEntityScope, React.ComponentType<{ className?: string }>> = {
  WorkItem: TextCompareIcon,
  Intent: EditSquareIcon,
  Project: ProjectIcon,
}

function WorkflowScopeIcon({ scope }: { scope: string | null }) {
  const Icon = scope && scope in SCOPE_ICON_MAP
    ? SCOPE_ICON_MAP[scope as LinkedEntityScope]
    : WorkflowDefinitionOutlineIcon
  return <Icon className="h-4 w-4 text-accent-soft shrink-0" />
}

type AnalyticsTab = 'token-analytics'

const ANALYTICS_TABS: { key: AnalyticsTab; label: string }[] = [
  { key: 'token-analytics', label: 'Token Analytics' },
]

type Provider = 'claude' | 'copilot'

const PROVIDER_TABS: { key: Provider; label: string }[] = [
  { key: 'claude', label: 'Claude Code' },
  { key: 'copilot', label: 'Copilot CLI' },
]

type Period = 'this-month' | 'last-month' | 'custom'
type View = 'by-workflow' | 'by-user'

function formatLocalDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getDateRange(period: Period, customFrom: string, customTo: string) {
  const now = new Date()

  if (period === 'this-month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1)
    return {
      from: formatLocalDate(from),
      to: formatLocalDate(now),
    }
  }

  if (period === 'last-month') {
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0)
    return {
      from: formatLocalDate(firstDay),
      to: formatLocalDate(lastDay),
    }
  }

  return {
    from: customFrom || undefined,
    to: customTo || undefined,
  }
}

function formatTokensK(tokens: number): string {
  const k = tokens / 1000
  if (k >= 100) return `${Math.round(k).toLocaleString('en-US')} K`
  return `${k.toFixed(1)} K`
}

function budgetIndicatorColor(pct: number): string {
  if (pct >= 90) return 'bg-destructive'
  if (pct >= 75) return 'bg-amber-500'
  return 'bg-button'
}

function formatCost(cost: number): string {
  return `$ ${cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function AnalyticsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const canViewAnalytics = hasPermission(Permissions.Project.Analytics.Read, projectId)

  const [period, setPeriod] = useState<Period>('this-month')
  const [view, setView] = useState<View>('by-workflow')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [provider, setProvider] = useState<Provider>('claude')
  const providerUserSelected = useRef(false)

  const copilotAvailabilityQuery = useProjectCopilotAuthAvailability(projectId)
  const copilotAvailable = copilotAvailabilityQuery.data?.available
  
  useEffect(() => {
    if (providerUserSelected.current) return
    if (copilotAvailable === undefined) return
    setProvider(copilotAvailable ? 'copilot' : 'claude')
  }, [copilotAvailable])

  const dateRange = getDateRange(period, customFrom, customTo)

  const workflowQuery = useTokenAnalyticsByWorkflow(projectId!, dateRange, { enabled: provider === 'claude' })
  const userQuery = useTokenAnalyticsByUser(projectId!, dateRange, { enabled: provider === 'claude' })
  const copilotWorkflowQuery = useCopilotAnalyticsByWorkflow(projectId!, dateRange, { enabled: provider === 'copilot' })
  const copilotUserQuery = useCopilotAnalyticsByUser(projectId!, dateRange, { enabled: provider === 'copilot' })

  if (authLoading) return null
  if (!canViewAnalytics) return <Navigate to={`/projects/${projectId}/overview`} replace />

  const workflowData = workflowQuery.data
  const userData = userQuery.data
  const copilotWorkflowData = copilotWorkflowQuery.data
  const copilotUserData = copilotUserQuery.data

  const claudeBudgetPct = workflowData?.spendingCapUsd
    ? Math.min((workflowData.totalCostUsd / workflowData.spendingCapUsd) * 100, 100)
    : 0

  const copilotBudgetPct = copilotWorkflowData?.spendingCapUsd
    ? Math.min((copilotWorkflowData.totalCostUsd / copilotWorkflowData.spendingCapUsd) * 100, 100)
    : 0

  const handleProviderChange = (p: Provider) => {
    providerUserSelected.current = true
    setProvider(p)
  }

  return (
    <div>
      <ProjectSectionHeader sectionName="Analytics" />

      {/* Top tabs */}
      <TabBar
        tabs={ANALYTICS_TABS}
        activeTab="token-analytics"
        onTabChange={() => {}}
        className="w-fit mt-5 mb-6"
      />

      {/* Provider tabs: Claude Code / Copilot CLI */}
      <TabBar
        tabs={PROVIDER_TABS}
        activeTab={provider}
        onTabChange={handleProviderChange}
        className="w-fit mb-6"
      />

      {/* Period + view toggle on same row */}
      <div className="flex items-center gap-3 mb-6">
        {/* Period dropdown with calendar icon */}
        <div className="relative w-44">
          <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
          <Select value={period} onValueChange={v => setPeriod(v as Period)}>
            <SelectTrigger aria-label="Period" className="pl-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this-month">This month</SelectItem>
              <SelectItem value="last-month">Last month</SelectItem>
              <SelectItem value="custom">Custom period</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {period === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customFrom}
              onChange={e => setCustomFrom(e.target.value)}
              aria-label="From date"
              data-testid="custom-date-from"
              className="h-10 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <span className="text-sm text-muted-foreground">–</span>
            <input
              type="date"
              value={customTo}
              onChange={e => setCustomTo(e.target.value)}
              aria-label="To date"
              data-testid="custom-date-to"
              className="h-10 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )}

        {/* View toggle */}
        <div className="inline-flex rounded-lg border border-border-accent bg-toolbar p-1 gap-1">
          <button
            onClick={() => setView('by-workflow')}
            className={cn(
              'flex items-center gap-2 px-5 h-8 text-sm font-medium rounded-lg transition-colors',
              view === 'by-workflow'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <ByProjectIcon className="h-4 w-4 shrink-0" />
            By Workflow
          </button>
          <button
            onClick={() => setView('by-user')}
            className={cn(
              'flex items-center gap-2 px-5 h-8 text-sm font-medium rounded-lg transition-colors bg-toolbar',
              view === 'by-user'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <ByUsersIcon className="h-4 w-4 shrink-0" />
            By User
          </button>
        </div>
      </div>

      {/* Claude Code tab content */}
      {provider === 'claude' && (
        <>
          {/* By Workflow view */}
          {view === 'by-workflow' && (
            <div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <Card data-testid="total-token-card" className="rounded-2xl">
                  <CardContent className="pt-6 pb-6 flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium mb-1">Total Token Used</p>
                      <p className="text-[32px] font-semibold">
                        {workflowData
                          ? workflowData.totalTokens.toLocaleString('en-US')
                          : '—'}
                      </p>
                    </div>
                    <div className="h-16 w-16 rounded-xl bg-border flex items-center justify-center shrink-0">
                      <TokenKeyIcon className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="monthly-budget-card" className="rounded-2xl">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <p className="text-sm font-medium mb-1">Monthly Budget</p>
                        {workflowData?.spendingCapUsd != null && workflowData.spendingCapUsd > 0 ? (
                          <p className="text-[32px] font-semibold">
                            $ {workflowData.totalCostUsd.toFixed(2)}
                            <span className="text-base font-normal text-muted-foreground">
                              {' '}
                              /{workflowData.spendingCapUsd.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">No budget set</p>
                        )}
                      </div>
                      <div className="h-16 w-16 rounded-xl bg-border flex items-center justify-center shrink-0">
                        <MoneyIcon className="h-6 w-6" />
                      </div>
                    </div>
                    {workflowData?.spendingCapUsd && (
                      <>
                        <Progress value={claudeBudgetPct} className="h-[4px]" indicatorClassName={budgetIndicatorColor(claudeBudgetPct)} />
                        <div className="flex justify-between text-sm mt-1.5">
                          <span className="text-foreground font-bold">
                            {workflowData.totalCostUsd.toFixed(2)} /{' '}
                            {workflowData.spendingCapUsd.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                          <span className="text-muted-foreground">{Math.round(claudeBudgetPct)}% used</span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {workflowQuery.isError ? (
                <div role="alert" className="text-sm text-destructive py-4">Failed to load analytics data.</div>
              ) : workflowQuery.isLoading ? (
                <div role="status" aria-live="polite" className="text-sm text-muted-foreground py-4">
                  Loading...
                </div>
              ) : (
                <div className="rounded-2xl border overflow-hidden">
                  <table aria-label="Token consumption per workflow" className="w-full table-fixed break-words bg-table-row text-card-foreground">
                    <thead>
                      <tr className="bg-table-header border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground w-1/3">
                          Workflow
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          Tokens Used
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          Input Token Usage
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          Output Token Usage
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          Cached Token Usage
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-foreground">
                          Amount ($)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {workflowData?.rows.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="workflow-empty-state">
                            No usage recorded for this period.
                          </td>
                        </tr>
                      )}
                      {workflowData?.rows.map((row) => (
                        <tr
                          key={row.workflowDefinitionId ?? `${row.sourceCategory}-${row.displayName}`}
                          className="border-b last:border-b-0 hover:bg-muted/40 transition-colors"
                          data-testid="workflow-row"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {row.sourceCategory === 'chat' ? (
                                <MessageSquareText className="h-4 w-4 text-muted-foreground shrink-0" />
                              ) : row.sourceCategory === 'agent' ? (
                                <Brain className="h-4 w-4 text-muted-foreground shrink-0" />
                              ) : (
                                <WorkflowScopeIcon scope={row.linkedEntityScope} />
                              )}
                              <span className="min-w-0 text-sm">{row.displayName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">{formatTokensK(row.totalTokens)}</td>
                          <td className="px-4 py-3 text-sm">{formatTokensK(row.inputTokens)}</td>
                          <td className="px-4 py-3 text-sm">{formatTokensK(row.outputTokens)}</td>
                          <td className="px-4 py-3 text-sm">{formatTokensK(row.cachedTokens)}</td>
                          <td className="px-4 py-3 text-sm text-right">{formatCost(row.costUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {workflowData && (
                      <tfoot>
                        <tr className="border-t bg-table-row shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.15)]">
                          <td className="px-4 py-3 text-sm font-semibold">Total</td>
                          <td className="px-4 py-3 text-sm font-semibold">{formatTokensK(workflowData.totalTokens)}</td>
                          <td className="px-4 py-3 text-sm font-semibold">{formatTokensK(workflowData.rows.reduce((s, r) => s + r.inputTokens, 0))}</td>
                          <td className="px-4 py-3 text-sm font-semibold">{formatTokensK(workflowData.rows.reduce((s, r) => s + r.outputTokens, 0))}</td>
                          <td className="px-4 py-3 text-sm font-semibold">{formatTokensK(workflowData.rows.reduce((s, r) => s + r.cachedTokens, 0))}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-right">
                            {formatCost(workflowData.totalCostUsd)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}

          {/* By User view */}
          {view === 'by-user' && (
            <div>
              {userQuery.isError ? (
                <div role="alert" className="text-sm text-destructive py-4">Failed to load analytics data.</div>
              ) : userQuery.isLoading ? (
                <div role="status" aria-live="polite" className="text-sm text-muted-foreground py-4">
                  Loading...
                </div>
              ) : (
                <div className="rounded-2xl border overflow-hidden">
                  <table aria-label="Token usage by user" className="w-full table-fixed break-words bg-table-row text-card-foreground">
                    <thead>
                      <tr className="bg-table-header border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground w-1/3">
                          User
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          Tokens Used
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          Input Token Usage
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          Output Token Usage
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          Cached Token Usage
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-foreground">
                          Amount ($)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {userData?.users.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="user-empty-state">
                            No usage recorded for this period.
                          </td>
                        </tr>
                      )}
                      {userData?.users.map((user) => (
                        <tr
                          key={user.userId}
                          className="border-b last:border-b-0 hover:bg-muted/40 transition-colors"
                          data-testid="user-row"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-9 w-9 shrink-0">
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                                  {user.initials}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex min-w-0 flex-col" data-testid="stacked-identity">
                                <span className="text-sm font-medium leading-tight">{user.displayName}</span>
                                <span className="text-xs text-muted-foreground">{user.email}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">{formatTokensK(user.totalTokens)}</td>
                          <td className="px-4 py-3 text-sm">{formatTokensK(user.inputTokens)}</td>
                          <td className="px-4 py-3 text-sm">{formatTokensK(user.outputTokens)}</td>
                          <td className="px-4 py-3 text-sm">{formatTokensK(user.cachedTokens)}</td>
                          <td className="px-4 py-3 text-sm text-right">{formatCost(user.costUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {userData && (
                      <tfoot>
                        <tr className="border-t bg-table-row shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.15)]">
                          <td className="px-4 py-3 text-sm font-semibold">Total</td>
                          <td className="px-4 py-3 text-sm font-semibold">{formatTokensK(userData.totalTokens)}</td>
                          <td className="px-4 py-3 text-sm font-semibold">{formatTokensK(userData.users.reduce((s, u) => s + u.inputTokens, 0))}</td>
                          <td className="px-4 py-3 text-sm font-semibold">{formatTokensK(userData.users.reduce((s, u) => s + u.outputTokens, 0))}</td>
                          <td className="px-4 py-3 text-sm font-semibold">{formatTokensK(userData.users.reduce((s, u) => s + u.cachedTokens, 0))}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-right">
                            {formatCost(userData.totalCostUsd)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Copilot CLI tab content */}
      {provider === 'copilot' && (
        <>
          {/* By Workflow view */}
          {view === 'by-workflow' && (
            <div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <Card data-testid="copilot-monthly-budget-card" className="rounded-2xl">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div>
                        <p className="text-sm font-medium mb-1">Monthly Budget</p>
                        {copilotWorkflowData?.spendingCapUsd != null && copilotWorkflowData.spendingCapUsd > 0 ? (
                          <p className="text-[32px] font-semibold">
                            $ {copilotWorkflowData.totalCostUsd.toFixed(2)}
                            <span className="text-base font-normal text-muted-foreground">
                              {' '}
                              /{copilotWorkflowData.spendingCapUsd.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">No budget set</p>
                        )}
                      </div>
                      <div className="h-16 w-16 rounded-xl bg-border flex items-center justify-center shrink-0">
                        <MoneyIcon className="h-6 w-6 " />
                      </div>
                    </div>
                    {copilotWorkflowData?.spendingCapUsd && (
                      <>
                        <Progress value={copilotBudgetPct} className="h-[4px]" indicatorClassName={budgetIndicatorColor(copilotBudgetPct)} />
                        <div className="flex justify-between text-sm mt-1.5">
                          <span className="text-foreground font-bold">
                            {copilotWorkflowData.totalCostUsd.toFixed(2)} /{' '}
                            {copilotWorkflowData.spendingCapUsd.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                          <span className="text-muted-foreground">{Math.round(copilotBudgetPct)}% used</span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
                <div />
              </div>

              {copilotWorkflowQuery.isError ? (
                <div role="alert" className="text-sm text-destructive py-4">Failed to load analytics data.</div>
              ) : copilotWorkflowQuery.isLoading ? (
                <div role="status" aria-live="polite" className="text-sm text-muted-foreground py-4">
                  Loading...
                </div>
              ) : (
                <div className="rounded-2xl border overflow-hidden">
                  <table aria-label="AI credit consumption per workflow" className="w-full table-fixed break-words bg-table-row text-card-foreground">
                    <thead>
                      <tr className="bg-table-header border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground w-1/2">
                          Workflow
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-foreground">
                          Amount ($)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {copilotWorkflowData?.rows.length === 0 && (
                        <tr>
                          <td colSpan={2} className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="copilot-workflow-empty-state">
                            No usage recorded for this period.
                          </td>
                        </tr>
                      )}
                      {copilotWorkflowData?.rows.map((row) => (
                        <tr
                          key={row.workflowDefinitionId ?? row.displayName}
                          className="border-b last:border-b-0 hover:bg-muted/40 transition-colors"
                          data-testid="copilot-workflow-row"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <WorkflowScopeIcon scope={row.linkedEntityScope} />
                              <span className="min-w-0 text-sm">{row.displayName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-right">{formatCost(row.costUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {copilotWorkflowData && (
                      <tfoot>
                        <tr className="border-t bg-table-row shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.15)]">
                          <td className="px-4 py-3 text-sm font-semibold">Total</td>
                          <td className="px-4 py-3 text-sm font-semibold text-right">
                            {formatCost(copilotWorkflowData.totalCostUsd)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}

          {/* By User view */}
          {view === 'by-user' && (
            <div>
              {copilotUserQuery.isError ? (
                <div role="alert" className="text-sm text-destructive py-4">Failed to load analytics data.</div>
              ) : copilotUserQuery.isLoading ? (
                <div role="status" aria-live="polite" className="text-sm text-muted-foreground py-4">
                  Loading...
                </div>
              ) : (
                <div className="rounded-2xl border overflow-hidden">
                  <table aria-label="AI credit usage by user" className="w-full table-fixed break-words bg-table-row text-card-foreground">
                    <thead>
                      <tr className="bg-table-header border-b">
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground w-1/2">
                          User
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-foreground">
                          Amount ($)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {copilotUserData?.users.length === 0 && (
                        <tr>
                          <td colSpan={2} className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="copilot-user-empty-state">
                            No usage recorded for this period.
                          </td>
                        </tr>
                      )}
                      {copilotUserData?.users.map((user) => (
                        <tr
                          key={user.userId}
                          className="border-b last:border-b-0 hover:bg-muted/40 transition-colors"
                          data-testid="copilot-user-row"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-9 w-9 shrink-0">
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                                  {user.initials}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex min-w-0 flex-col" data-testid="stacked-identity">
                                <span className="text-sm font-medium leading-tight">{user.displayName}</span>
                                <span className="text-xs text-muted-foreground">{user.email}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-right">{formatCost(user.costUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {copilotUserData && (
                      <tfoot>
                        <tr className="border-t bg-table-row shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.15)]">
                          <td className="px-4 py-3 text-sm font-semibold">Total</td>
                          <td className="px-4 py-3 text-sm font-semibold text-right">
                            {formatCost(copilotUserData.totalCostUsd)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
